import axios from 'axios';
import api from './api';

export interface Training {
  id: string;
  title: string;
  banner_image?: string;
  country?: {
    id: number;
    name: string;
  };
  city?: string;

  wilaya?: {
    id: number;
    name: string;
  };

  reference?: string;
  domaine_id: number;
  subcategory_id?: string;
  format?: string;
  level?: string;
  duration?: string;
  schedule_date: string;
  full_description?: string;
  learning_objectives?: string;
  skills_gained?: string;
  target_audience?: string;
  detailed_program_pdf?: string;
  trainer_profile_snippets?: string;
  schedule_dates?: string;
  pricing_dzd?: number;
  trainer?: string;
  language?: string;
  views?: number;
  rating?: number;
  reviews?: number;
  isApproved?: boolean;
  isActive?: boolean;
  is_active: boolean;
  is_featured?: boolean;
  rejectionReason?: string;
  status?: 'pending' | 'approved' | 'refused';
  rejection_reason?: string;
  domaine?: {
    id: number;
    name?: string;
    name_french?: string;
    name_arabic?: string;
    name_english?: string;
  };
  category?: {
    id: string;
    name_french: string;
    name_english: string;
  };
  subcategory?: {
    id: string;
    name_french: string;
    name_english: string;
  };
  field?: {
    id: number;
    name_french: string;
    name_english: string;
  };
  training_center?: {
    id: number;
    name: string;
    country?: {
      id: number;
      name: string;
    };
    wilaya?: {
      id: number;
      name: string;
    };
    city?: string;
  };
  ratingStats?: {
    averageRating: number;
    totalReviews: number;
    ratingDistribution: { [key: number]: number };
  };
  viewCount?: number;
  sessions?: {
    id: number;
    training_id: number;
    session_date: string;
  }[];
}

export interface Review {
  id: number;
  training_id: number;
  user_id: number;
  rating: number;
  comment: string;
  helpful_count: number;
  is_verified: boolean;
  created_at: string;
  updated_at: string;
  user: {
    id: number;
    username: string;
    firstName?: string;
    lastName?: string;
  };
}

export interface CreateReviewData {
  rating: number;
  comment: string;
}

export interface CreateTrainingData {
  title: string;
  banner_image?: string;
  country_id?: number;
  city?: string;
  wilaya_id?: number;
  reference?: string;
  domaine_id: number;
  subcategory_id?: string;
  format?: string;
  level?: string;
  duration?: string;
  schedule_date?: string;
  full_description?: string;
  learning_objectives?: string;
  skills_gained?: string;
  target_audience?: string;
  detailed_program_pdf?: string;
  trainer_profile_snippets?: string;
  schedule_dates?: string;
  pricing_dzd?: number;
  trainer?: string;
  language?: string;
}

export interface TrainingStats {
  total: number;
  active: number;
  completed: number;
}

export const trainingsService = {
  // Get all trainings for the current training center
  getAllTrainings: async (): Promise<Training[]> => {
    try {
      const response = await api.get('/trainings');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get training statistics
  getTrainingStats: async (): Promise<TrainingStats> => {
    try {
      const response = await api.get('/trainings/stats');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get dashboard statistics
  getDashboardStats: async (): Promise<{
    totalTrainings: number;
    activeTrainings: number;
    totalSessions: number;
    totalSubscriptions: number;
    totalRevenue: number;
    pendingInvoices: number;
  }> => {
    try {
      const response = await api.get('/trainings/dashboard-stats');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get training center statistics (views, ratings, reviews, sessions)
  getCenterStats: async (): Promise<{
    totalViews: number;
    averageRating: number;
    totalReviews: number;
    activeSessions: number;
    completedSessions: number;
    pendingApprovals: number;
  }> => {
    try {
      const response = await api.get('/trainings/center-stats');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get a single training by ID
  getTraining: async (id: string): Promise<Training> => {
    try {
      const response = await api.get(`/trainings/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Create a new training
  createTraining: async (data: CreateTrainingData, files?: { banner?: File; pdf?: File }): Promise<Training> => {
    try {
      const formData = new FormData();

      // Add all form data with proper handling
      Object.keys(data).forEach(key => {
        const value = data[key as keyof CreateTrainingData];
        if (value !== undefined && value !== null && value !== '') {
          // Don't send domaine_id if it's 0 (not selected)
          if (key === 'domaine_id' && value === 0) {
            return; // Skip this field
          }
          formData.append(key, String(value));
        }
      });

      // Add files if provided
      if (files?.banner) {
        formData.append('files', files.banner);
      }
      if (files?.pdf) {
        formData.append('files', files.pdf);
      }

      const response = await api.post('/trainings', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw error;
      }
      throw error;
    }
  },

  // Update a training
  updateTraining: async (id: string, data: Partial<CreateTrainingData>, files?: { banner?: File; pdf?: File }): Promise<Training> => {
    try {
      const formData = new FormData();

      // Add all form data
      Object.keys(data).forEach(key => {
        if (data[key as keyof CreateTrainingData] !== undefined && data[key as keyof CreateTrainingData] !== '') {
          formData.append(key, String(data[key as keyof CreateTrainingData]));
        }
      });

      // Add files if provided
      if (files?.banner) {
        formData.append('files', files.banner);
      }
      if (files?.pdf) {
        formData.append('files', files.pdf);
      }

      const response = await api.patch(`/trainings/${id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const apiError = error.response?.data;
        throw new Error(apiError?.message || 'Error updating training');
      }
      throw error;
    }
  },

  // Delete a training
  deleteTraining: async (id: string): Promise<void> => {
    try {
      await api.delete(`/trainings/${id}`);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const apiError = error.response?.data;
        throw new Error(apiError?.message || 'Error deleting training');
      }
      throw error;
    }
  },

  // Get featured trainings
  getFeaturedTrainings: async (): Promise<Training[]> => {
    try {
      const url = '/public/trainings/featured';

      const response = await api.get(url);

      return response.data;
    } catch (error: any) {
      throw error;
    }
  },

  // Get training by ID (public)
  getTrainingPublic: async (id: string): Promise<Training> => {
    try {
      const response = await api.get(`/public/trainings/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Review methods
  getTrainingReviews: async (trainingId: string): Promise<Review[]> => {
    try {
      const response = await api.get(`/trainings/${trainingId}/reviews`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  createReview: async (trainingId: string, data: CreateReviewData): Promise<Review> => {
    try {
      const response = await api.post(`/trainings/${trainingId}/reviews`, data);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const apiError = error.response?.data;
        throw new Error(apiError?.message || 'Error creating review');
      }
      throw error;
    }
  },

  markReviewHelpful: async (trainingId: string, reviewId: number): Promise<void> => {
    try {
      await api.post(`/trainings/${trainingId}/reviews/${reviewId}/helpful`);
    } catch (error) {
      throw error;
    }
  },

  // View tracking methods
  trackTrainingView: async (trainingId: string): Promise<void> => {
    try {
      await api.post(`/trainings/${trainingId}/views/track`);
    } catch (error) {
      // Don't throw error for view tracking as it's not critical
    }
  },

  getTrainingViewCount: async (trainingId: string): Promise<number> => {
    try {
      const response = await api.get(`/trainings/${trainingId}/views/count`);
      return response.data.count;
    } catch (error) {
      return 0;
    }
  },

  // Subscription helpers for training centers
  getSubscriptionPlans: async () => {
    const response = await api.get('/subscriptions/plans');
    return response.data;
  },

  updateSubscriptionPlan: async (planId: number, planData: any) => {
    const response = await api.patch(`/admin/subscriptions/plans/${planId}`, planData);
    return response.data;
  },

  submitSubscriptionRequest: async (planId: number, receipt: File) => {
    const form = new FormData();
    form.append('receipt', receipt);
    const response = await api.post(`/subscriptions/request/${planId}`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  getCurrentSubscription: async (centerId?: number) => {
    const url = centerId ? `/subscriptions/current?centerId=${centerId}` : '/subscriptions/current';
    const response = await api.get(url);
    return response.data;
  },

  // Pre-check if the center can create another training
  canCreateTraining: async (): Promise<{ canCreate: boolean; message?: string }> => {
    const response = await api.get('/trainings/can-create');
    return response.data;
  },

  // Invoices Management
  getInvoices: async (page: number = 1, limit: number = 10): Promise<{ invoices: any[]; total: number }> => {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    const response = await api.get(`/payment-receipts/invoices?${params}`);
    return response.data;
  },

  downloadInvoice: async (filename: string): Promise<Blob> => {
    const response = await api.get(`/payment-receipts/invoices/download/${filename}`, {
      responseType: 'blob',
    });
    return response.data;
  },

  // Session management
  createSession: async (trainingId: string, sessionData: { session_date: string }): Promise<any> => {
    try {
      const response = await api.post(`/trainings/${trainingId}/sessions`, sessionData);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const apiError = error.response?.data;
        throw new Error(apiError?.message || 'Error creating session');
      }
      throw error;
    }
  },

  // Participants management
  getParticipants: async (filters?: {
    trainingId?: string;
    sessionId?: string;
    status?: string;
    search?: string;
    page?: number;
    limit?: number;
  }): Promise<{
    participants: any[];
    stats: {
      total: number;
      inProgress: number;
      completed: number;
      certified: number;
    };
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  }> => {
    try {
      const params = new URLSearchParams();
      if (filters?.trainingId) params.append('trainingId', filters.trainingId);
      if (filters?.sessionId) params.append('sessionId', filters.sessionId);
      if (filters?.status) params.append('status', filters.status);
      if (filters?.search) params.append('search', filters.search);
      if (filters?.page) params.append('page', filters.page.toString());
      if (filters?.limit) params.append('limit', filters.limit.toString());

      const response = await api.get(`/trainings/participants?${params}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getParticipantDetails: async (participantId: number): Promise<any> => {
    try {
      const response = await api.get(`/trainings/participants/${participantId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getSessions: async (trainingId?: string): Promise<any[]> => {
    try {
      const params = new URLSearchParams();
      if (trainingId) params.append('trainingId', trainingId);

      const response = await api.get(`/trainings/sessions?${params}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Statistics-based filtering methods
  getPopularTrainings: async (): Promise<Training[]> => {
    try {
      const response = await api.get('/trainings/popular');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getBestRatedTrainings: async (): Promise<Training[]> => {
    try {
      const response = await api.get('/trainings/best-rated');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getMostAttendedTrainings: async (): Promise<Training[]> => {
    try {
      const response = await api.get('/trainings/most-attended');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getTrainingStatistics: async (): Promise<{
    popular: Training[];
    bestRated: Training[];
    mostAttended: Training[];
  }> => {
    try {
      const response = await api.get('/trainings/statistics');
      return response.data;
    } catch (error) {
      throw error;
    }
  }
}; 